
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config";

/** Base URL of the company-scoped post-login API. */
const COMPANY = config.POSTLOGIN_API_URL_COMPANY;

/** Base URL of the KPI frequency routes. */
const KPI_FREQUENCY = `${COMPANY}kpiFrequency`;

/** The batched read: every selected site's cadences in one request. */
const KPI_FREQUENCY_BULK = `${KPI_FREQUENCY}/bulk`;

/**
 * Lists the frameworks the company reports against.
 * @param {number} userId the signed-in user.
 * @returns {Promise<object>} apiCall result whose data is the framework array.
 */
export const listFrameworks = (userId) =>
  apiCall(`${COMPANY}getFramework`, {}, { type: "ALL", userId }, "GET");

/**
 * Lists the quantitative KPIs a frequency can be set against.
 *
 * The same call Data Management makes, deliberately: a cadence only means
 * something for a KPI that receives data there, so the two screens must list
 * the same set.
 * @param {number} financialYearId the financial year.
 * @param {number[]} frameworkIds the company's frameworks.
 * @returns {Promise<object>} apiCall result whose data.data is the KPI array.
 */
export const listQuantitativeKpis = (financialYearId, frameworkIds) =>
  apiCall(
    `${COMPANY}getReportingQuestionnew`,
    {},
    {
      financialYearId,
      frameworkIds,
      question_type_filter: JSON.stringify(["quantitative"]),
    },
    "GET",
  );

/**
 * Lists the company's locations with their sub-locations.
 *
 * The same call Data Management makes, so the sites a cadence can be set for are
 * exactly the sites data is entered against.
 * @returns {Promise<object>} apiCall result whose data.data is the location array.
 */
export const listLocations = () => apiCall(`${COMPANY}getSource`, {}, {}, "GET");

/**
 * Reads the configured frequencies for a financial year across every site given
 * — the only read path.
 *
 * One request for a whole selection rather than one per site: a company with
 * forty locations would otherwise open the page with forty parallel calls plus a
 * baseline read. `data.results` holds one entry per site in the order asked for,
 * each carrying that site's own `kpiFrequencyMap` and its `effectiveFrequencyMap`
 * including what it inherits. `data.baseline.kpiFrequencyMap` is the company-wide
 * cadence a site with no setting of its own falls back to, and
 * `data.defaultFrequency` / `data.startMonth` belong to the year rather than to
 * any site.
 *
 * An empty selection still reads the company-wide scope, so a caller can ask for
 * the year's own values without naming a site.
 * @param {number} financialYearId the financial year.
 * @param {Array<{locationId: number, subLocationId: number}>} [scopes] the sites to read.
 * @returns {Promise<object>} apiCall result whose data holds a result per site.
 */
export const listKpiFrequencies = (financialYearId, scopes) =>
  apiCall(KPI_FREQUENCY_BULK, {}, { financialYearId, scopes: scopeQuery(scopes) }, "GET");

/**
 * Applies many frequency changes across many sites in one request — the only
 * write path.
 *
 * Entries with a frequency are set at every scope given; entries with
 * `frequency: null` clear it there so the KPI inherits the broader cadence
 * again. One request rather than one per site, so a cadence applied to a
 * selection of locations either lands everywhere or nowhere. Rejected entries
 * come back in `data.skipped` rather than failing the batch. The 6th arg
 * suppresses the global alert because the page renders its own summary banner
 * with the saved/cleared/skipped counts.
 * @param {number} financialYearId the financial year.
 * @param {Array<{kpiId: number, frequency: string|null}>} frequencies the changes.
 * @param {Array<{locationId: number, subLocationId: number}>} scopes the sites to write.
 * @returns {Promise<object>} apiCall result with `saved`, `cleared` and `skipped`.
 */
export const saveKpiFrequencies = (financialYearId, frequencies, scopes) =>
  apiCall(
    KPI_FREQUENCY_BULK,
    {},
    { financialYearId: Number(financialYearId), frequencies, scopes: scopeList(scopes) },
    "POST",
    true,
    false,
  );

/**
 * Resets the configured frequencies for a financial year, at every site given.
 *
 * Those sites go back to inheriting the broader cadence and every other site
 * keeps its own; at the company-wide scope every scope in the year goes, which
 * is what "reset all to default" means there.
 * @param {number} financialYearId the financial year.
 * @param {Array<{locationId: number, subLocationId: number}>} scopes the sites to reset.
 * @returns {Promise<object>} apiCall result carrying how many were cleared.
 */
export const resetKpiFrequencies = (financialYearId, scopes) =>
  apiCall(
    `${KPI_FREQUENCY}/bulk/reset`,
    {},
    { financialYearId: Number(financialYearId), scopes: scopeList(scopes) },
    "POST",
    true,
    false,
  );

/**
 * Turns a selection of sites into the batched read's `scopes` parameter.
 *
 * Sites go over as `locationId` or `locationId_subLocationId`, comma separated —
 * the encoding the screen's own dropdown values already use, so the URL carries
 * no second format. The company-wide scope is dropped rather than sent as a `0`
 * sentinel: an empty parameter is how the endpoint reads "the whole company",
 * and mixing the sentinel in with real sites would ask for a result nothing maps
 * back to.
 * @param {Array<{locationId: number, subLocationId: number}>} [scopes] the sites.
 * @returns {string} the parameter value; empty for the company-wide scope.
 */
function scopeQuery(scopes) {
  return scopeList(scopes)
    .map((scope) => (scope.subLocationId
      ? `${scope.locationId}_${scope.subLocationId}`
      : `${scope.locationId}`))
    .join(",");
}

/**
 * Turns a selection of sites into the write endpoints' `scopes` body.
 *
 * The company-wide scope is dropped rather than sent as `{ locationId: 0 }`: an
 * empty list is how both endpoints read "the whole company", and mixing the
 * sentinel in with real locations would write the company-wide row as well as
 * the sites the person picked.
 * @param {Array<{locationId: number, subLocationId: number}>} scopes the sites.
 * @returns {Array<{locationId: number, subLocationId: number}>} the body entries.
 */
function scopeList(scopes) {
  return (Array.isArray(scopes) ? scopes : [])
    .map((scope) => ({
      locationId: Number(scope?.locationId) || 0,
      subLocationId: Number(scope?.subLocationId) || 0,
    }))
    .filter((scope) => scope.locationId > 0);
}
